import { useState, useEffect, useRef, useCallback } from 'react'

export const useWebSocketData = () => {
  const [data, setData] = useState(null)
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState(null)
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState('disconnected')
  
  const wsRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const heartbeatIntervalRef = useRef(null)
  const heartbeatTimeoutRef = useRef(null)
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = Infinity // Tentar reconectar indefinidamente
  const deviceIdRef = useRef(null)
  const lastHeartbeatRef = useRef(Date.now())
  const lastPongRef = useRef(Date.now())
  const isConnectingRef = useRef(false)
  const forcedReconnectRef = useRef(false)
  const connectionLockRef = useRef(false) // Lock para evitar conexões simultâneas

  // Configurações de heartbeat e reconexão
  const HEARTBEAT_INTERVAL = 30000 // 30 segundos (voltando ao original, mais estável)
  const HEARTBEAT_TIMEOUT = 20000 // 20 segundos para aguardar pong (mais generoso)
  const INITIAL_RECONNECT_DELAY = 2000 // 2 segundos
  const MAX_RECONNECT_DELAY = 15000 // 15 segundos (mais agressivo)
  const AGGRESSIVE_RECONNECT_DELAY = 3000 // 3 segundos para reconexão agressiva após timeout

  // Função para abrir o modal manualmente
  const openAuthModal = () => setShowAuthModal(true)
  const closeAuthModal = () => setShowAuthModal(false)

  // Gerar ID único do dispositivo (persistente)
  const getDeviceId = useCallback(() => {
    if (deviceIdRef.current) return deviceIdRef.current
    
    let deviceId = localStorage.getItem('dashboard_device_id')
    if (!deviceId) {
      deviceId = generateDeviceId()
      localStorage.setItem('dashboard_device_id', deviceId)
    }
    
    deviceIdRef.current = deviceId
    return deviceId
  }, [])

  const generateDeviceId = () => {
    const timestamp = Date.now().toString(36)
    const random = Math.random().toString(36).substring(2)
    const userAgent = navigator.userAgent.substring(0, 10)
    return `${timestamp}-${random}-${userAgent}`.replace(/[^a-zA-Z0-9-]/g, '').substring(0, 32)
  }

  // Função para calcular delay de reconexão com backoff exponencial
  const getReconnectDelay = useCallback(() => {
    // Se for uma reconexão forçada por timeout, usar delay agressivo
    if (forcedReconnectRef.current) {
      forcedReconnectRef.current = false
      return AGGRESSIVE_RECONNECT_DELAY
    }
    
    // Backoff exponencial normal, mas limitado
    const delay = Math.min(
      INITIAL_RECONNECT_DELAY * Math.pow(1.5, Math.min(reconnectAttempts.current, 8)),
      MAX_RECONNECT_DELAY
    )
    // Adicionar jitter para evitar thundering herd
    return delay + Math.random() * 1000
  }, [])

  // Função para lidar com erros de conexão (definida primeiro)
  const handleConnectionError = useCallback((errorMessage) => {
    setError(errorMessage)
    setConnected(false)
    setConnectionStatus('error')
    
    // Parar heartbeat
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current)
      heartbeatTimeoutRef.current = null
    }
    
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
  }, [])

  // Função para iniciar heartbeat
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current)
    }

    console.log('Iniciando sistema de heartbeat')
    
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try {
          console.log('Enviando ping...')
          wsRef.current.send(JSON.stringify({ type: 'ping' }))
          lastHeartbeatRef.current = Date.now()
          
          // Configurar timeout para aguardar pong (mais generoso)
          heartbeatTimeoutRef.current = setTimeout(() => {
            const timeSinceLastPong = Date.now() - lastPongRef.current
            if (timeSinceLastPong > HEARTBEAT_TIMEOUT && connected) {
              console.warn(`Timeout no heartbeat detectado. Última resposta há ${timeSinceLastPong}ms`)
              forcedReconnectRef.current = true
              handleConnectionError('Timeout no heartbeat - sem resposta do servidor')
            }
          }, HEARTBEAT_TIMEOUT)
          
        } catch (error) {
          console.warn('Erro ao enviar heartbeat:', error)
          if (connected) {
            forcedReconnectRef.current = true
            handleConnectionError('Erro no heartbeat')
          }
        }
      }
    }, HEARTBEAT_INTERVAL)
    
    // Inicializar timestamp da última resposta
    lastPongRef.current = Date.now()
  }, [connected, handleConnectionError])

  // Função para parar heartbeat
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current)
      heartbeatTimeoutRef.current = null
    }
  }, [])

  // Função para verificar se o heartbeat está respondendo
  const checkHeartbeat = useCallback(() => {
    if (!connected) return
    
    const now = Date.now()
    const timeSinceLastPong = now - lastPongRef.current
    const timeSinceLastPing = now - lastHeartbeatRef.current
    
    // Se passou muito tempo desde o último pong e estamos conectados
    if (timeSinceLastPong > (HEARTBEAT_INTERVAL + HEARTBEAT_TIMEOUT) && connected) {
      console.warn(`Heartbeat perdido. Última resposta há ${timeSinceLastPong}ms, último ping há ${timeSinceLastPing}ms`)
      forcedReconnectRef.current = true
      handleConnectionError('Conexão perdida - heartbeat não respondendo')
    }
  }, [connected, handleConnectionError])

  // Função para conectar (definida antes de reconnect para evitar dependência circular)
  const connect = useCallback(() => {
    // Verificação rigorosa para evitar conexões múltiplas
    if (connectionLockRef.current || 
        isConnectingRef.current || 
        wsRef.current?.readyState === WebSocket.CONNECTING ||
        wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('Conexão já em andamento ou ativa, ignorando nova tentativa')
      return
    }

    try {
      // Adquirir lock de conexão
      connectionLockRef.current = true
      isConnectingRef.current = true
      setConnectionStatus('connecting')
      setError(null)
      
      // Limpar conexão anterior se existir
      if (wsRef.current) {
        try {
          wsRef.current.close()
        } catch (e) {
          console.warn('Erro ao fechar conexão anterior:', e)
        }
        wsRef.current = null
      }
      
      // Usar localhost para desenvolvimento e teste
      const wsUrl = import.meta.env.VITE_WS_URL || 'wss://back.ws.ege.net.br/ws/dashboard/'
      console.log('Tentando conectar ao WebSocket:', wsUrl)
      
      wsRef.current = new WebSocket(wsUrl)
      
      // Timeout para conexão
      const connectionTimeout = setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
          console.warn('Timeout na conexão WebSocket')
          wsRef.current.close()
          connectionLockRef.current = false
          isConnectingRef.current = false
          handleConnectionError('Timeout na conexão')
        }
      }, 10000) // 10 segundos de timeout
      
      wsRef.current.onopen = () => {
        clearTimeout(connectionTimeout)
        console.log('WebSocket conectado com sucesso')
        setConnected(true)
        setError(null)
        setConnectionStatus('connected')
        reconnectAttempts.current = 0
        connectionLockRef.current = false
        isConnectingRef.current = false
        
        // Enviar identificação do dispositivo
        const deviceId = getDeviceId()
        const deviceName = `TV Dashboard - ${window.location.hostname}`
        
        try {
          wsRef.current.send(JSON.stringify({
            type: 'device_identification',
            device_id: deviceId,
            device_name: deviceName
          }))
        } catch (e) {
          console.error('Erro ao enviar identificação:', e)
        }
        
        // Iniciar heartbeat após um pequeno delay
        setTimeout(() => {
          startHeartbeat()
        }, 1000)
      }
      
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          
          switch (message.type) {
            case 'device_identification_required':
            // Já foi enviado no onopen
              break
              
            case 'device_authorized':
            setIsAuthorized(true)
            setShowAuthModal(false)
            console.log('Dispositivo já autorizado')
              break
              
            case 'authorization_required':
            setShowAuthModal(true)
            setIsAuthorized(false)
              break
              
            case 'authorization_success':
            setIsAuthorized(true)
            setShowAuthModal(false)
              break
              
            case 'authorization_failed':
            setError('Código de autorização inválido')
              break
              
            case 'dashboard_data':
            setData(message.data)
              break
              
            case 'pong':
              // Atualizar timestamp do último heartbeat
              lastPongRef.current = Date.now()
              console.log('Pong recebido - conexão ativa')
              
              // Limpar timeout de heartbeat se existir
              if (heartbeatTimeoutRef.current) {
                clearTimeout(heartbeatTimeoutRef.current)
                heartbeatTimeoutRef.current = null
              }
              break
              
            case 'error':
              console.error('Erro do servidor:', message.message)
              setError(message.message)
              break
              
            default:
              console.log('Mensagem recebida:', message)
          }
        } catch (err) {
          console.error('Erro ao processar mensagem:', err)
        }
      }
      
      wsRef.current.onclose = (event) => {
        console.log('WebSocket fechado:', event.code, event.reason)
        setConnected(false)
        setConnectionStatus('disconnected')
        stopHeartbeat()
        connectionLockRef.current = false
        isConnectingRef.current = false
        
        // Não reconectar se foi fechamento intencional
        if (event.code === 1000) {
          console.log('Conexão fechada intencionalmente')
          return
        }
        
        // Evitar reconexão imediata se já há uma tentativa agendada
        if (reconnectTimeoutRef.current) {
          console.log('Reconexão já agendada, ignorando...')
          return
        }
        
        // Sempre tentar reconectar (removido limite de tentativas)
        console.log(`Conexão perdida (código: ${event.code}). Tentativa de reconexão ${reconnectAttempts.current + 1}`)
        
        const delay = getReconnectDelay()
        console.log(`Reagendando reconexão em ${delay}ms...`)
        
        setConnectionStatus('reconnecting')
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null
          reconnectAttempts.current++
          connect()
        }, delay)
      }
      
      wsRef.current.onerror = (error) => {
        console.error('Erro no WebSocket:', error)
        connectionLockRef.current = false
        isConnectingRef.current = false
        handleConnectionError('Erro na conexão WebSocket')
      }
      
    } catch (err) {
      console.error('Erro ao criar conexão:', err)
      connectionLockRef.current = false
      isConnectingRef.current = false
      handleConnectionError('Erro ao criar conexão')
    }
  }, [getDeviceId, startHeartbeat, stopHeartbeat, handleConnectionError, getReconnectDelay])

  // Função para reconectar (agora definida depois de connect)
  const reconnect = useCallback(() => {
    if (connectionLockRef.current || isConnectingRef.current) {
      console.log('Reconexão já em andamento, ignorando...')
      return
    }

    console.log('Iniciando reconexão manual...')
    
    // Limpar timeout anterior se existir
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    
    forcedReconnectRef.current = true
    
    // Fechar conexão atual se existir
    if (wsRef.current) {
      try {
        wsRef.current.close(1000, 'Reconexão manual')
      } catch (e) {
        console.warn('Erro ao fechar conexão:', e)
      }
      wsRef.current = null
    }
    
    // Conectar após um pequeno delay
    setTimeout(() => {
      connect()
    }, 1000)
  }, [connect])

  // Função para desconectar
  const disconnect = useCallback(() => {
    console.log('Desconectando WebSocket...')
    
    // Limpar todos os timeouts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    
    stopHeartbeat()
    
    // Resetar flags
    connectionLockRef.current = false
    isConnectingRef.current = false
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Desconexão intencional')
      wsRef.current = null
    }
    
    setConnected(false)
    setConnectionStatus('disconnected')
    reconnectAttempts.current = 0
  }, [stopHeartbeat])

  // Função para enviar código de autorização
  const sendAuthCode = useCallback((code) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'authorization_code',
        code: code
      }))
    }
  }, [])

  // Função para lidar com sucesso na autorização
  const handleAuthSuccess = useCallback((code) => {
    sendAuthCode(code)
  }, [sendAuthCode])

  // Função para solicitar atualização dos dados
  const requestUpdate = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'request_update'
      }))
    }
  }, [])

  // Efeito para iniciar conexão
  useEffect(() => {
    console.log('Iniciando conexão WebSocket...')
    connect()
    
    // Verificar heartbeat menos frequentemente para reduzir overhead
    const heartbeatCheckInterval = setInterval(checkHeartbeat, 5000) // A cada 5 segundos
    
    return () => {
      console.log('Limpando conexão WebSocket...')
      disconnect()
      clearInterval(heartbeatCheckInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Dependências vazias para executar apenas uma vez

  // Efeito para limpar timeouts quando o componente é desmontado
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current)
      }
    }
  }, [])

  return {
    data,
    connected,
    error,
    isAuthorized,
    showAuthModal,
    connectionStatus,
    openAuthModal,
    closeAuthModal,
    handleAuthSuccess,
    disconnect,
    requestUpdate,
    reconnect
  }
}
